import React from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';

import { useActionSheet } from '@components/ActionSheet';
import { UserAvatar } from '@components/Avatar';
import Divider from '@components/Divider';
import Explorer from '@components/Explorer';
import type { SearchTargets } from '@components/Explorer';
import Icon from '@components/Icon';
import IconButton from '@components/IconButton';
import Modal from '@components/Modal';
import Placeholder from '@components/Placeholders';
import Pressable, { PressableOpacity } from '@components/Pressable';
import ScreenContainer from '@components/ScreenContainer';
import { Paragraph, Subheading } from '@components/Typography';
import FollowButton from '@components/User/FollowButton';
import MessageButton from '@components/User/MessageButton';
import { useBlockUserAction } from '@components/User/useBlockUserAction';
import { Row } from '@components/View';
import { useFollowing } from '@context/FollowingContext';
import { useAppTheme } from '@context/ThemeContext';
import { listenToUser } from '@db/Users';
import { formatNumber } from '@helpers/Formatters';
import { logError } from '@helpers/Loggers';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { AppNavigation, createAppNavigation } from '@navigation/AppNavigation';
import { CurrentUserProfileScreenProps, ProfileScreenProps } from '@navigation/Types';
import { User } from '@types';

export default function ProfileScreen({ route, navigation }: ProfileScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  const currentUserId = getCurrentUserId();
  const profileUserId = route.params.userId;
  const blockUserAction = useBlockUserAction(profileUserId);
  const actionSheetCallback = React.useCallback(
    (selectedIndex?: number) => {
      if (selectedIndex === 0) {
        blockUserAction.onPress();
      }
    },
    [blockUserAction]
  );
  const { showActionSheet } = useActionSheet(
    {
      options: [blockUserAction.actionLabel, 'Cancel'],
      cancelButtonIndex: 1,
    },
    actionSheetCallback
  );

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () =>
        profileUserId !== currentUserId ? (
          <IconButton
            icon="dots-vertical"
            onPress={() => showActionSheet()}
            testID="navigation.Header.ProfileActionsIconButton"
          />
        ) : null,
    });
  }, [currentUserId, navigation, profileUserId, showActionSheet]);

  return <BaseProfileScreen appNavigation={appNavigation} route={route} />;
}

export function CurrentUserProfileScreen({ route, navigation }: CurrentUserProfileScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  const actionSheetCallback = React.useCallback(
    (selectedIndex?: number) => {
      switch (selectedIndex) {
        case 0:
          appNavigation.openEditCurrentUser();
          return;
        case 1:
          appNavigation.openSettings();
          return;
      }
    },
    [appNavigation]
  );
  const { showActionSheet } = useActionSheet(
    {
      options: ['Edit Profile', 'Settings', 'Cancel'],
      cancelButtonIndex: 2,
    },
    actionSheetCallback
  );

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <IconButton
          icon="dots-vertical"
          onPress={() => showActionSheet()}
          testID="navigation.Header.ProfileActionsIconButton"
        />
      ),
      headerLeft: undefined,
    });
  }, [navigation, showActionSheet]);

  return <BaseProfileScreen appNavigation={appNavigation} route={route} />;
}

type BaseProfileScreenProps = {
  appNavigation: AppNavigation;
  route: ProfileScreenProps['route'] | CurrentUserProfileScreenProps['route'];
};

const AVATAR_SIZE = 80;
const PROFILE_SEARCH_TARGETS: SearchTargets = { users: false, groups: false, places: true };

export function BaseProfileScreen({ appNavigation, route }: BaseProfileScreenProps) {
  const { followingUserIds } = useFollowing();
  const appTheme = useAppTheme();
  const currentUserId = getCurrentUserId();
  const routeUserId = route.params?.userId || currentUserId;
  const userId = routeUserId;
  const [userDoc, setUserDoc] = React.useState<User | undefined>(undefined);
  const isCurrentUser = userId === currentUserId;
  const isViewable = isCurrentUser || Boolean(userDoc?.public) || followingUserIds.has(userId);
  const [error, setError] = React.useState<boolean>(false);
  const [showImageModal, setShowImageModal] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (userId) {
      return listenToUser(
        userId,
        (user) => {
          setUserDoc(user);
        },
        (error) => {
          setError(true);
          logError(error);
        }
      );
    }
  }, [userId]);

  const renderHeader = (userDoc: User) => (
    <Row>
      <PressableOpacity onPress={() => setShowImageModal(true)} style={styles.avatarContainer}>
        <UserAvatar
          displayName={userDoc.displayName}
          photoUrl={userDoc.photoUrl}
          size={AVATAR_SIZE}
        />
      </PressableOpacity>
      <View style={styles.flex}>
        <Row>
          <Icon name="account-outline" style={styles.icon} />
          <Subheading
            numberOfLines={1}
            style={[styles.flex, styles.displayNameText]}
            testID="screens.ProfileScreen.Text.DisplayName">
            {userDoc.displayName}
          </Subheading>
        </Row>
        <Row>
          <Icon name="home-outline" style={styles.icon} />
          <Paragraph
            numberOfLines={1}
            style={styles.flex}
            testID="screens.ProfileScreen.Text.Hometown">
            {userDoc.hometown.displayName}
          </Paragraph>
        </Row>
        <PressableOpacity
          onPress={() => appNavigation.openUserFollows(userDoc.userId, userDoc.displayName)}>
          <Row>
            <Icon name="account-multiple-outline" style={styles.icon} />
            <Paragraph numberOfLines={1} style={styles.flex}>{`${formatNumber(
              userDoc.numFollowers || 0
            )} Followers · ${formatNumber(userDoc.numFollowing || 0)} Following`}</Paragraph>
          </Row>
        </PressableOpacity>
      </View>
    </Row>
  );

  const renderImageModal = () => {
    if (!userDoc) return null;

    const width = Math.min(Dimensions.get('screen').width, appTheme.maxWidth) * 0.9;
    return (
      <Modal visible={showImageModal} onDismiss={() => setShowImageModal(false)}>
        <Pressable onPress={() => setShowImageModal(false)}>
          <View style={styles.imageModalContainer}>
            <UserAvatar
              displayName={userDoc?.displayName}
              photoUrl={userDoc?.photoUrl}
              size={width}
            />
          </View>
        </Pressable>
      </Modal>
    );
  };

  const renderButtons = (userDoc: User) => (
    <Row style={styles.buttonContainer}>
      <FollowButton userId={userId} style={styles.buttons} />
      <MessageButton userDoc={userDoc} style={styles.buttons} onPress={appNavigation.openChat} />
    </Row>
  );

  if (!userDoc) {
    return <ScreenContainer loading />;
  }

  return (
    <ScreenContainer
      error={error}
      testID="screens.ProfileScreen.Background"
      style={styles.background}>
      {renderImageModal()}
      <View style={styles.profileHeader}>
        {renderHeader(userDoc)}
        {!isCurrentUser ? renderButtons(userDoc) : null}
      </View>
      <Divider style={styles.headerDivider} testID="screens.ProfileScreen.HeaderDivider" />
      {isViewable ? (
        <Explorer
          mapTestID="screens.ProfileScreen.Map"
          onChatPress={appNavigation.openChat}
          onCreatePlanPress={appNavigation.openCreatePlan}
          onGroupPress={appNavigation.openGroup}
          onPlanNavigationPress={appNavigation.openPlan}
          onUserNavigationPress={appNavigation.openProfile}
          queryUserIds={[userDoc.userId]}
          searchTargets={PROFILE_SEARCH_TARGETS}
        />
      ) : (
        <Placeholder
          icon="lock"
          title="Private profile"
          message="Follow to view"
          style={styles.placeholder}
        />
      )}
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  avatarContainer: {
    padding: 10,
  },
  background: {
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  buttonContainer: {
    alignSelf: 'center',
    justifyContent: 'space-evenly',
    width: '90%',
  },
  buttons: {
    width: '40%',
  },
  displayNameText: {
    fontWeight: 'bold',
  },
  flex: {
    flex: 1,
  },
  headerDivider: {
    alignSelf: 'center',
    marginVertical: 8,
    width: '80%',
  },
  icon: {
    paddingHorizontal: 5,
  },
  imageModalContainer: {
    alignSelf: 'center',
    justifyContent: 'center',
  },
  placeholder: {
    flex: 1,
    justifyContent: 'center',
    width: '100%',
  },
  profileHeader: {
    flexShrink: 0,
    width: '100%',
  },
});
